/**
 * The ONE booking query layer (reservation-dashboard-ui Tech-decision "View architecture"): all four
 * views read from these hooks, so switching view re-presents the SAME dataset and there is ONE place
 * wiring realtime invalidation. Every key is `["venue", venueId, "bookings", …]`, which the shell's
 * `eventQueryKeyMap` invalidates on `booking.*` and reconnect-snapshot (VenueRealtimeProvider) — so a
 * create/edit/cancel from another device refetches here within ~1–2s without a refresh (R3/R4).
 */
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import type {
  CalendarDensityResponse,
  PageResponseStaffBookingSummaryResponse,
} from "@repo/api-client";
import { floorApi, floorKeys, type FloorPlanResponse } from "../features/floor-plan/floorApi";
import { scheduleApi, scheduleKeys, type ServiceResponse } from "../features/schedule/scheduleApi";
import { bookingsApi } from "./bookingsApi";
import { filterKey, isNarrowed, type BookingFilter } from "./searchParams";

const PAGE_LIMIT = 25;

export const bookingKeys = {
  /** The whole family — the prefix `booking.*` realtime events invalidate. */
  all: (venueId: string) => ["venue", venueId, "bookings"] as const,
  list: (venueId: string, filter: BookingFilter) =>
    ["venue", venueId, "bookings", "list", filterKey(filter)] as const,
  calendar: (venueId: string, from: string, to: string, filter: BookingFilter) =>
    ["venue", venueId, "bookings", "calendar", from, to, filterKey(filter)] as const,
  inbox: (venueId: string) => ["venue", venueId, "bookings", "inbox"] as const,
};

type Page = PageResponseStaffBookingSummaryResponse;

/** Cursor-paginated list (ADR-0022). Disabled until the filter narrows (wide-range guard). */
export function useBookingsList(
  venueId: string,
  filter: BookingFilter,
): UseInfiniteQueryResult<InfiniteData<Page>, unknown> {
  return useInfiniteQuery({
    queryKey: bookingKeys.list(venueId, filter),
    enabled: isNarrowed(filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      bookingsApi.list(venueId, filter, pageParam, PAGE_LIMIT, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** Per-day/status densities for the calendar view over a day range. */
export function useCalendarDensity(
  venueId: string,
  from: string,
  to: string,
  filter: BookingFilter,
) {
  return useQuery<CalendarDensityResponse[]>({
    queryKey: bookingKeys.calendar(venueId, from, to, filter),
    queryFn: ({ signal }) =>
      bookingsApi.calendar(venueId, from, to, filter.status, filter.channel, signal),
  });
}

/** The PENDING request inbox (read-only). */
export function useInbox(venueId: string): UseInfiniteQueryResult<InfiniteData<Page>, unknown> {
  return useInfiniteQuery({
    queryKey: bookingKeys.inbox(venueId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => bookingsApi.inbox(venueId, pageParam, PAGE_LIMIT, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/**
 * The venue floor read model (§C.5), reused for the timeline resource rows and the floor SVG.
 * Keyed `["venue", venueId, "floorplan"]` (the floor feature's family), so `floor.*`/`table.*`
 * events keep it live too. A CAPACITY_ONLY plan drives the degraded zone-lane rendering.
 */
export function useFloorPlan(venueId: string) {
  return useQuery<FloorPlanResponse>({
    queryKey: floorKeys.plan(venueId),
    queryFn: () => floorApi.getPlan(venueId),
    staleTime: 60_000,
  });
}

/** The venue's services (§C.4), reused for the manual-entry/edit service picker. */
export function useServices(venueId: string) {
  return useQuery<ServiceResponse[]>({
    queryKey: scheduleKeys.services(venueId),
    queryFn: () => scheduleApi.listServices(venueId),
    staleTime: 60_000,
  });
}

/** Flatten an infinite bookings result to a single row list. */
export function flattenPages(data: InfiniteData<Page> | undefined) {
  return (data?.pages ?? []).flatMap((p) => p.items ?? []);
}
